import {
  PRIORITY_LABELS,
  SEVERITY_LABELS,
  STATUS_LABELS as WORK_STATUS_LABELS,
  STATUS_ORDER,
  TRANSITION_REASONS,
  TYPE_LABELS,
} from "../workitems/workItemVocabulary";
import { SPRINT_STATUS_LABELS } from "../sprints/sprintVocabulary";
import { STATUS_LABELS as PROJECT_STATUS_LABELS } from "../projects/projectVocabulary";
import { optionsFrom, type GuideChapter } from "./guideTypes";

/** Everyday delivery: what the work is, where it sits, and how it moves. */
export const DELIVERY_CHAPTER: GuideChapter = {
  id: "delivery",
  title: "Everyday delivery",
  sections: [
    {
      id: "getting-around",
      title: "Getting around",
      where: "The bar across the top, and the menu down the left",
      purpose:
        "The left menu is grouped by who uses it: Work is your day, Organisation is people and "
        + "time, Administration is settings most people never touch. The top bar carries the "
        + "things you need wherever you happen to be.",
      examples: [
        {
          title: "Raising a bug while you are looking at something else",
          steps: [
            "You are reading a timesheet and remember a defect nobody has written down.",
            "Press New item in the top bar — it is there on every screen for exactly this.",
            "The project is already filled in if you are inside one. Pick Bug, give it a title.",
            "Everything else can wait. A bug with a title is more useful than one still in "
            + "somebody's head.",
          ],
        },
      ],
      tables: [
        {
          title: "The top bar, left to right",
          options: [
            {
              name: "New item",
              meaning:
                "Creates work from anywhere. Uses the project you are currently in, or asks.",
            },
            {
              name: "Notifications (bell)",
              meaning:
                "Things that happened to your work — assignments, mentions, approvals waiting on "
                + "you, releases you are named on.",
            },
            {
              name: "Light and dark (moon or sun)",
              meaning: "Switches the theme. Remembered on this device.",
            },
            {
              name: "User guide (question mark)",
              meaning: "This page.",
            },
            {
              name: "Your initials",
              meaning:
                "Your account, and sign out. Sign out everywhere ends every session on every "
                + "device, which is what to use if you have lost a laptop.",
            },
          ],
        },
      ],
    },

    {
      id: "projects",
      title: "Projects",
      where: "Projects in the left menu",
      purpose:
        "A project is a body of work with a budget, a plan and people. Everything else — work "
        + "items, sprints, releases — belongs to one. The tabs across a project are the different "
        + "questions you can ask about it.",
      examples: [
        {
          title: "Finding out whether a project is in trouble",
          steps: [
            "Open the project. The header shows its status and a health badge.",
            "Overview compares what was planned against what has happened: hours approved, hours "
            + "burnt, the forecast finish against the baseline finish.",
            "Health is calculated, but somebody can override it — if it has been overridden, the "
            + "reason is shown beside it. A red project with no explanation is different from one "
            + "a manager has looked at and marked red deliberately.",
            "Scope changes lists everything added or removed since the baseline was signed off, "
            + "which is usually where an overrun actually came from.",
          ],
        },
      ],
      tables: [
        {
          title: "Project status",
          intro: "Where the project is in its own life, not how it is going.",
          options: optionsFrom(PROJECT_STATUS_LABELS, {
            DRAFT: "Being set up. Not yet real work.",
            PLANNING: "Being scoped and estimated.",
            PENDING_APPROVAL: "A baseline has been submitted and is waiting for sign-off.",
            APPROVED: "Signed off, not yet started.",
            ACTIVE: "Running.",
            ON_HOLD: "Paused deliberately. Time is not expected to be booked to it.",
            COMPLETED: "Delivered.",
            CANCELLED: "Stopped before delivery.",
            ARCHIVED: "Closed and out of the way. Still readable.",
          }),
        },
        {
          title: "Health",
          intro: "How it is going, which is a different question from status.",
          options: [
            { name: "Green", meaning: "On plan." },
            { name: "Amber", meaning: "Slipping, or forecast to exceed the baseline." },
            { name: "Red", meaning: "Will not make the plan without a decision." },
          ],
        },
        {
          title: "The tabs",
          options: [
            { name: "Overview", meaning: "Budget, forecast and the plan against reality." },
            { name: "Work", meaning: "Every work item in the project, filterable." },
            { name: "Members", meaning: "Who is on it, in what role, and since when." },
            {
              name: "Baselines",
              meaning:
                "The signed-off plan, and every revision of it. A baseline cannot be edited once "
                + "approved — a change means a new version, which is what makes the comparison "
                + "mean anything.",
            },
            {
              name: "Scope changes",
              meaning: "Everything added or removed since the baseline, with its reason.",
            },
            { name: "Milestones", meaning: "The dates the project is judged against." },
            { name: "Releases", meaning: "The versions this project ships, and how they went." },
          ],
        },
      ],
    },

    {
      id: "work-items",
      title: "Work items",
      where: "A project's Work tab, Backlog, Search, or My Work",
      purpose:
        "The unit of work. Six types, arranged in three layers: an epic holds stories, tasks, "
        + "bugs and spikes; those hold subtasks. What makes an item useful is a title somebody "
        + "else can act on and a status that is true.",
      examples: [
        {
          title: "A story through its whole life",
          steps: [
            "Create a Story: \"Pay by bank transfer\". It lands in the backlog at the bottom — "
            + "arriving later is not the same as mattering more.",
            "Planning pulls it into a sprint. Somebody picks it up: Start progress.",
            "They finish the code: Send to code review. A reviewer asks for changes — the "
            + "reviewer moves it back and must say why, and \"The review asked for changes\" is "
            + "recorded on the item for ever.",
            "Second time round the review passes: Approve review, then Start testing.",
            "QA finds nothing: Pass QA. The item is Done.",
            "The whole path is on the Activity tab, with who did what and when.",
          ],
        },
        {
          title: "Linking a bug to the release that fixes it",
          steps: [
            "Open the bug. The Releases panel on the right says which release carries it.",
            "Press Set, choose the release, save. It is now in that release's scope.",
            "If that release's scope is already frozen, you are asked why it is going in anyway — "
            + "that answer appears on the release's scope-change ledger.",
          ],
        },
      ],
      tables: [
        {
          title: "Types",
          options: optionsFrom(TYPE_LABELS, {
            EPIC: "A container for related work. Not worked on directly.",
            USER_STORY: "Something a user can do that they could not before.",
            TECHNICAL_TASK: "Work with no direct user-visible outcome — a migration, an upgrade.",
            BUG: "Something that does not work as intended. Carries a severity.",
            SPIKE: "Time-boxed investigation. The output is an answer, not a feature.",
            SUBTASK: "A slice of one of the above. Always has a parent.",
            ANALYSIS: "Investigation that ends in a written document, not code.",
            DEMO_PREPARATION: "Getting something ready to show. Ends in a screenshot as proof.",
            MIGRATION: "A one-off change run directly on a database. The queries are the evidence.",
            SERVER_WORK: "Setting up, configuring, deploying to or maintaining a server. Ends in a screenshot as proof.",
            CLIENT_SERVICE: "Time spent with a client rather than on the product \u2014 explaining it, training them, answering what they rang about. Cannot be completed until the call is recorded: who, which client, how you reached them, and what you told them.",
            MEETING: "A meeting that happened. Created from its minutes so the hours spent in it land somewhere.",
          }),
        },
        {
          title: "Statuses",
          intro: "In board order. You never set these directly — you pick a named move.",
          options: optionsFrom(
            WORK_STATUS_LABELS,
            {
              BACKLOG: "Not started and not committed to.",
              SELECTED: "Committed to, not yet begun.",
              IN_PROGRESS: "Somebody is working on it now.",
              DEV_DONE: "The code is finished. Nobody has reviewed it yet.",
              CODE_REVIEW: "With a reviewer now.",
              READY_FOR_QA: "Reviewed, waiting for a tester.",
              QA: "Being tested.",
              DONE: "Finished and accepted.",
              CANCELLED: "Will not be done. Kept, not deleted.",
            },
            STATUS_ORDER,
          ),
        },
        {
          title: "Priority",
          options: optionsFrom(PRIORITY_LABELS, {
            CRITICAL: "Drop other things for this.",
            HIGH: "Next after anything critical.",
            MEDIUM: "Normal.",
            LOW: "When there is room.",
          }),
        },
        {
          title: "Bug severity",
          intro:
            "How bad the defect is, which is not the same as how soon you will fix it — that is "
            + "priority. Blocker and Critical are the two that hold a release back.",
          options: optionsFrom(SEVERITY_LABELS, {
            BLOCKER: "Nothing else can proceed. Holds a release until somebody accepts the risk.",
            CRITICAL: "Major function broken with no workaround. Also holds a release.",
            MAJOR: "Significant, but there is a way round it.",
            MINOR: "Noticeable and tolerable.",
            TRIVIAL: "Cosmetic.",
          }),
        },
        {
          title: "Why work moved backwards",
          intro:
            "Asked whenever an item goes back a step or is cancelled. Moving forwards never asks "
            + "— a reason field full of \"n/a\" is worse than no reason field.",
          options: TRANSITION_REASONS.map(([code, label]) => ({
            name: label,
            meaning:
              code === "OTHER"
                ? "Anything the list does not cover. This one demands a sentence, because the "
                  + "code on its own explains nothing."
                : "",
          })).map((option) =>
            option.meaning === ""
              ? { ...option, meaning: "Recorded on the item and readable months later." }
              : option,
          ),
        },
      ],
      notes: [
        "You cannot set a status directly. The Move drawer offers only the moves that are legal "
        + "from where the item is, and says what each one will do — so the screen and the rules "
        + "can never disagree.",
        "A subtask must have a parent. An epic can never be one.",
        "Cancelled work is kept, not deleted. \"Why isn't that in the release?\" needs an answer.",
      ],
    },

    {
      id: "backlog",
      title: "Backlog",
      where: "Backlog in the left menu",
      purpose:
        "Everything not yet in a sprint, in priority order. Epics are left out — they are "
        + "containers, and a list holding both an epic and its stories reads as though they were "
        + "separate things to schedule.",
      examples: [
        {
          title: "Planning a sprint from the backlog",
          steps: [
            "Sort the backlog by dragging. The order is the team's answer to \"what next\".",
            "Select the items you are committing to and use Move to sprint.",
            "The sprint's committed points are fixed at the moment it starts — anything added "
            + "afterwards is counted separately, so mid-sprint additions cannot quietly become "
            + "part of what was promised.",
          ],
        },
      ],
      tables: [],
    },

    {
      id: "sprints",
      title: "Sprints",
      where: "Sprints in the left menu",
      purpose:
        "A fixed period with a commitment. The board shows work by status; the metrics show "
        + "whether the commitment held.",
      examples: [
        {
          title: "Running a sprint",
          steps: [
            "Create the sprint with its dates. It is Planned.",
            "Move work in from the backlog, then Start it. What is in it at that moment becomes "
            + "the commitment.",
            "During the sprint, drag cards across the board — or use the move button on a card, "
            + "which does the same thing and works without a mouse.",
            "Each card shows its release version, if it is promised to one.",
            "Complete the sprint at the end. Unfinished work goes back to the backlog rather "
            + "than disappearing, and the sprint's metrics are frozen.",
          ],
        },
      ],
      tables: [
        {
          title: "Sprint status",
          options: optionsFrom(SPRINT_STATUS_LABELS, {
            PLANNED: "Created, not started. Dates can still change.",
            ACTIVE: "Running. The commitment is fixed.",
            COMPLETED: "Finished. Metrics are recorded and cannot be edited.",
            CANCELLED: "Called off. Records no metrics, so it cannot drag velocity down.",
          }),
        },
      ],
      notes: [
        "One team can only have one active sprint at a time. The database enforces it.",
        "Dates cannot be changed once a sprint has started.",
        "A cancelled sprint returns its work to the backlog.",
      ],
    },
  ],
};
